import React, { useMemo } from 'react';
import { useProjectPermissions } from '../../../services/projectPermissions';
import { Alert, Box, Button } from '@mui/material';
import { StatusToasts } from '../../common/StatusToast';
import { Refresh } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  useIngestionSummary,
  useProjectRepositories,
  useRerunUnderstanding,
  useUnderstandingStatus,
} from '../../../services/api';
import { phase0TabPath } from './projectPhases';
import FailedRepositoriesPanel, { failedKeyedRepositories } from './FailedRepositoriesPanel';

interface ProjectRecoveryBannerProps {
  projectId: string;
  projectStatus?: string;
  ingestionStatus?: string;
  ingestionError?: string;
  projectUpdatedAt?: string;
}

const INGEST_STUCK_MINUTES = 15;

const ProjectRecoveryBanner: React.FC<ProjectRecoveryBannerProps> = ({
  projectId,
  projectStatus,
  ingestionStatus,
  ingestionError,
  projectUpdatedAt,
}) => {
  const fastPoll = projectStatus === 'ingesting';
  const { data: summary } = useIngestionSummary(projectId, { fastPoll });
  const { data: repos = [] } = useProjectRepositories(projectId);
  const { data: understanding } = useUnderstandingStatus(projectId);
  const rerun = useRerunUnderstanding(projectId);
  // Re-running understanding starts project work (execute).
  const rerunPerms = useProjectPermissions(projectId);
  const canRerun = rerunPerms.can('execute');

  const fileCount = summary?.file_count ?? 0;
  const workers = understanding?.workers ?? [];
  const anyFailed = workers.some((w) => w.status === 'failed');
  const ingestFailed = projectStatus === 'failed' || ingestionStatus === 'failed';
  // A multi-repository project whose failure is one repository's: that
  // repository is retried or removed on its own. Re-uploading through Discover
  // would replace every repository of the project.
  const failedRepositories = failedKeyedRepositories(repos).length > 0;

  const minutesSinceUpdate = useMemo(() => {
    if (!projectUpdatedAt) return 0;
    return (Date.now() - new Date(projectUpdatedAt).getTime()) / 60_000;
  }, [projectUpdatedAt]);

  const scanningArchive =
    projectStatus === 'ingesting' &&
    fileCount === 0 &&
    (ingestionStatus === 'running' || ingestionStatus === undefined) &&
    minutesSinceUpdate < INGEST_STUCK_MINUTES;

  const ingestStuck =
    projectStatus === 'ingesting' &&
    fileCount === 0 &&
    minutesSinceUpdate >= INGEST_STUCK_MINUTES;

  const needsUpload =
    fileCount === 0 &&
    !scanningArchive &&
    !ingestStuck &&
    (projectStatus === 'planning' || (projectStatus === 'ingesting' && repos.length === 0));

  if (!scanningArchive && !ingestStuck && !needsUpload && !ingestFailed && !anyFailed) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      {scanningArchive && (
        <StatusToasts
          items={[
            {
              id: 'scan-archive',
              tone: 'loading',
              title: 'Scanning archive',
              detail: 'Large uploads can take several minutes before file counts appear. This page updates automatically.',
            },
          ]}
        />
      )}
      {ingestStuck && (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={
            <Button
              size="small"
              component={RouterLink}
              to={phase0TabPath(projectId, 'repo')}
            >
              Re-upload archive
            </Button>
          }
        >
          Ingestion has been running for over {INGEST_STUCK_MINUTES} minutes with no indexed files.
          Try uploading the archive again.
        </Alert>
      )}
      {needsUpload && (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={
            <Button
              size="small"
              component={RouterLink}
              to={phase0TabPath(projectId, 'repo')}
            >
              Upload archive
            </Button>
          }
        >
          No source code indexed yet. Upload a zip archive, or add repository URLs when creating the
          project.
        </Alert>
      )}
      {ingestFailed && failedRepositories && <FailedRepositoriesPanel projectId={projectId} repos={repos} />}
      {ingestFailed && !failedRepositories && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          action={
            <Button
              size="small"
              component={RouterLink}
              to={phase0TabPath(projectId, 'repo')}
            >
              Retry upload
            </Button>
          }
        >
          Discover ingestion failed
          {ingestionError ? `: ${ingestionError}` : ''}
          {repos.length > 0 ? ' — re-upload the archive or re-run the scan.' : '.'}
        </Alert>
      )}
      {anyFailed && (
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              startIcon={rerun.isPending ? undefined : <Refresh />}
              disabled={rerun.isPending || !canRerun}
            title={rerunPerms.reason('execute') ?? undefined}
              onClick={() => rerun.mutate()}
            >
              {rerun.isPending ? 'Rerunning…' : 'Rerun understanding'}
            </Button>
          }
        >
          One or more digital workers failed. Regenerate understanding to refresh diagrams and
          insights.
        </Alert>
      )}
    </Box>
  );
};

export default ProjectRecoveryBanner;
